import React, { useCallback, useMemo, useState } from 'react'

import clone from 'lodash.clonedeep'
import Button from '@shared/components/Button'
import Can from '@shared/components/Can'

import { meta as appModulesMeta } from '@modules/index'
import { NavLink, useHistory } from 'react-router-dom'
import { useAuth } from '@shared/hooks/auth'
import { Form } from '@unform/web'
import { Select } from '@shared/components/Form'
import { checkMenuPermission, getHomePage, menus } from './menus'

import {
  Container,
  NavigationHeader,
  NavigationInfo,
  NavigationItem,
  NavigationList,
  NavigationSubmenu,
  ScrollContainer
} from './styles'

const Navigation = () => {
  const formRef = React.useRef(null)
  const history = useHistory()

  const { user, unit, module, updateUnit, signOut, updateModule } = useAuth()

  const [currentSubmenu, setCurrentSubmenu] = useState('')

  const handleUnitSelection = React.useCallback(
    (selection) => {
      if (selection.unit) {
        const unit = user.units.find((unit) => unit.id === selection.unit.id)
        const module = unit?.modules?.find(Boolean)

        updateUnit(unit)
        updateModule(module)

        history.push(getHomePage(module))
      }
    },
    [history, updateModule, updateUnit, user]
  )

  const handleModuleSelection = React.useCallback(
    (selection) => {
      if (selection.module) {
        const module = unit.modules.find((module) => module.id === selection.module.id)
        updateModule(module)
        history.push(getHomePage(module))
      }
    },
    [history, updateModule, unit]
  )

  const unitOptions = React.useMemo(() => {
    return user?.units?.map((unit) => ({ id: unit.id, text: unit.name })) || []
  }, [user])

  const moduleOptions = React.useMemo(() => {
    return (
      unit?.modules?.map((module) => ({
        id: module.id,
        text: appModulesMeta[module.name].title
      })) || []
    )
  }, [unit])

  const handleLogout = useCallback(
    () => {
      signOut()
    },
    [signOut]
  )

  const handleSubmenuClick = useCallback(
    (name) => {
      return () => {
        currentSubmenu === name
          ? setCurrentSubmenu('')
          : setCurrentSubmenu(name)
      }
    },
    [currentSubmenu, setCurrentSubmenu]
  )

  const selectedModuleMenu = useMemo(() => {
    const menu = clone(menus[module?.name] || [])
    return menu.filter(checkMenuPermission(module))
  }, [module])

  return (
    Boolean(selectedModuleMenu.length) && (
      <Container className="navigation">
        <NavigationHeader>
          <img src={module.meta.logo} alt="Paggue Gestão" />
        </NavigationHeader>

        <ScrollContainer className="scrollarea scrollarea--tiny">
          {user?.units.length > 1 ? (
            <Form
              ref={formRef}
              initialData={{ unit }}
              onSubmit={handleUnitSelection}
              style={{ marginTop: '1rem' }}
            >
              <div className="navigation__select">
                <Select
                  name="unit"
                  label="Unidade"
                  placeholder=" "
                  options={unitOptions}
                  onChange={() => formRef.current.submitForm()}
                />
              </div>
            </Form>
          ) : (
            <Can perform="unit:list">
              <Form
                ref={formRef}
                initialData={{ unit }}
                onSubmit={handleUnitSelection}
                style={{ marginTop: '1rem' }}
              >
                <div className="navigation__select">
                  <Select
                    name="unit"
                    label="Unidade"
                    placeholder=" "
                    options={unitOptions}
                    onChange={() => formRef.current.submitForm()}
                  />
                </div>
              </Form>
            </Can>
          )}

          {unit.modules.length > 1 && (
            <Form
              ref={formRef}
              initialData={{ module }}
              onSubmit={handleModuleSelection}
            >
              <div className="navigation__select">
                <Select
                  name="module"
                  label="Módulo"
                  placeholder=" "
                  options={moduleOptions}
                  onChange={() => formRef.current.submitForm()}
                />
              </div>
            </Form>
          )}

          <NavigationList>
            {selectedModuleMenu.map(
              ({ title, info, url, icon: Icon, items }, menuIdx) =>
                items ? (
                  <NavigationSubmenu
                    key={`submenu-${menuIdx}`}
                    isActive={currentSubmenu === `submenu-${menuIdx}`}
                  >
                    <NavigationItem
                      onClick={handleSubmenuClick(`submenu-${menuIdx}`)}
                    >
                      <Icon size={20} />
                      {title}
                    </NavigationItem>

                    {items.map(({ title, info, url, icon: Icon }, itemIdx) => (
                      <NavigationItem
                        key={`submenu-item-${menuIdx}-${itemIdx}`}
                      >
                        <NavLink
                          exact
                          to={url}
                          className="navigation__link"
                          activeClassName="navigation__link--active"
                        >
                          <Icon size={20} />
                          {title}
                          <NavigationInfo>{info}</NavigationInfo>
                        </NavLink>
                      </NavigationItem>
                    ))}
                  </NavigationSubmenu>
                ) : (
                  <>
                    <NavigationItem key={`menu-item-${menuIdx}`}>
                      <NavLink
                        exact
                        to={url}
                        className="navigation__link"
                        activeClassName="navigation__link--active"
                      >
                        <Icon size={20} />
                        {title}
                        <NavigationInfo>{info}</NavigationInfo>
                      </NavLink>
                    </NavigationItem>
                  </>
                )
            )}

            <NavigationItem>
              <NavigationItem style={{ width: '100%', marginTop: 45 }}>
                <NavLink to="/" className="navigation__link">
                  <Button
                    onClick={handleLogout}
                    type="translucid"
                    className="sign-out-button"
                  >
                    Sair
                  </Button>
                </NavLink>
              </NavigationItem>
            </NavigationItem>
          </NavigationList>
        </ScrollContainer>
      </Container>
    )
  )
}

export default Navigation
